import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Calendar, X } from 'lucide-react';

/**
 * Sélecteur d'intervalle de dates (du JJ/MM/AAAA au JJ/MM/AAAA).
 * Filtre les écritures d'un tableau comptable.
 *
 * Props :
 *  - dateFrom: string YYYY-MM-DD ('' = pas de borne inférieure)
 *  - dateTo: string YYYY-MM-DD ('' = pas de borne supérieure)
 *  - onChange: ({ dateFrom, dateTo }) => void
 *  - color: palette (indigo/blue/green/amber/red), défaut indigo
 *  - compact: bool — version compacte horizontale
 *  - testIdPrefix: préfixe pour data-testid (ex. 'bilan-range')
 */
const PALETTES = {
  indigo: { border: 'border-indigo-300', text: 'text-indigo-700', bg: 'bg-indigo-50' },
  blue: { border: 'border-blue-300', text: 'text-blue-700', bg: 'bg-blue-50' },
  green: { border: 'border-green-300', text: 'text-green-700', bg: 'bg-green-50' },
  amber: { border: 'border-amber-300', text: 'text-amber-700', bg: 'bg-amber-50' },
  red: { border: 'border-red-300', text: 'text-red-700', bg: 'bg-red-50' },
};

export default function DateRangeFilter({
  dateFrom = '',
  dateTo = '',
  onChange,
  color = 'indigo',
  compact = false,
  testIdPrefix = 'date-range',
}) {
  const p = PALETTES[color] || PALETTES.indigo;
  const handleFrom = (v) => onChange?.({ dateFrom: v, dateTo });
  const handleTo = (v) => onChange?.({ dateFrom, dateTo: v });
  const reset = () => onChange?.({ dateFrom: '', dateTo: '' });
  const active = !!(dateFrom || dateTo);

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border ${p.border} ${active ? p.bg : 'bg-white'}`}
      data-testid={`${testIdPrefix}-container`}
    >
      <Calendar className={`h-4 w-4 ${p.text}`} />
      <Label className={`text-xs font-semibold ${p.text} hidden sm:inline`}>Du</Label>
      <Input
        type="date"
        value={dateFrom}
        onChange={(e) => handleFrom(e.target.value)}
        className={`h-8 w-36 text-xs ${p.border} bg-white text-gray-900 font-semibold`}
        data-testid={`${testIdPrefix}-from`}
      />
      <Label className={`text-xs font-semibold ${p.text} hidden sm:inline`}>au</Label>
      <Input
        type="date"
        value={dateTo}
        onChange={(e) => handleTo(e.target.value)}
        className={`h-8 w-36 text-xs ${p.border} bg-white text-gray-900 font-semibold`}
        data-testid={`${testIdPrefix}-to`}
      />
      {active && (
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={reset}
          className="h-7 px-2 text-xs text-gray-500 hover:text-red-600"
          data-testid={`${testIdPrefix}-reset`}
          title="Réinitialiser le filtre"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

/**
 * Filtre une liste par intervalle de dates (champ `dateField`, format YYYY-MM-DD).
 * Si dateFrom et dateTo sont tous deux vides, renvoie la liste inchangée.
 */
export function applyDateRange(rows, dateFrom, dateTo, dateField = 'date') {
  if (!Array.isArray(rows)) return [];
  if (!dateFrom && !dateTo) return rows;
  return rows.filter((r) => {
    const d = (r?.[dateField] || '').slice(0, 10);
    if (!d) return false;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });
}

/**
 * Convertit une date ISO/YYYY-MM-DD en JJ/MM/AAAA (FR).
 */
export function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
}
